<style lang="scss" scoped>
//
</style>

<template>
    <v-container
        class="ma-0 pa-12"
        fluid>
        <v-card
            :loading="servicesLoading"
            class="mx-auto rounded-xxl">
            <template slot="progress">
                <v-progress-linear
                    color="deep-purple"
                    height="10"
                    indeterminate />
            </template>

            <v-card-title
                class="d-flex flex-column align-start pa-12">
                <img
                    class="float-left"
                    height="58"
                    src="@/assets/logo_lightmode.avif"
                    alt="SkySQL" />
                <span class="text-h4 mt-4">
                    <template v-if="sortedServices?.length && currentStep === 'service-launch'">
                        <v-btn
                            class="mr-2"
                            outlined
                            fab
                            @click="currentStep = 'service-listing'">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                    </template>
                    <template v-if="currentStep === 'service-listing'">
                        Integrate SkySQL with Vercel
                    </template>
                    <template v-if="currentStep === 'service-launch'">
                        Launch a New Serverless Database
                    </template>
                    <template v-if="currentStep === 'access-confirmation'">
                        Confirm Access to Your Database
                    </template>
                    <template v-if="currentStep === 'success-screen'">
                        Vercel Integration Successful
                    </template>
                </span>
            </v-card-title>

            <v-card-text class="pa-12 pt-0">
                <v-row
                    class="ma-0">
                    <v-col
                        v-if="!servicesInitialized"
                        cols="12"
                        class="pa-0">
                        <!-- <InitializingMessage /> -->
                    </v-col>
                    <v-col
                        v-else-if="currentStep === 'access-confirmation'"
                        cols="12"
                        class="pa-0 wrapper-container"
                        style="width: 100%;">
                        <v-row>
                            <v-col
                                cols="12">
                                <p class="text-body-1">
                                    I acknowledge that I am giving Vercel access to my SkySQL DB instances.
                                </p>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="5">
                                <div class="align-start d-flex flex-column">
                                    <img
                                        class="my-4"
                                        height="108"
                                        src="@/assets/vercel-icon.svg"
                                        alt="Vercel" />
                                    <span class="font-weight-black primary--text text-button">
                                        Vercel Account
                                    </span>
                                </div>
                            </v-col>

                            <v-col
                                cols="2"
                                class="d-flex">
                                <v-icon class="text--primary text-h2">
                                    mdi-arrow-right-thick
                                </v-icon>
                            </v-col>

                            <v-col
                                cols="5"
                                class="pl-12">
                                <div class="align-start d-flex flex-column">
                                    <img
                                        class="my-4"
                                        height="108"
                                        src="@/assets/logo-symbol.avif"
                                        alt="SkySQL" />
                                    <span class="font-weight-black primary--text text-button">
                                        SkySQL DB Instances
                                    </span>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                cols="12"
                                class="py-12">
                                <p class="text-body-1">
                                    In order to remove this integration access to my SkySQL DB instances, I understand that I will need to manually take action to <a
                                        href="https://vercel.com/docs/integrations/manage-integration"
                                        target="_blank"
                                        rel="noreferrer">uninstall this Vercel Integration</a>.
                                </p>
                            </v-col>
                        </v-row>
                        <v-btn
                            class="launch-button text-none"
                            dark
                            block
                            x-large
                            rounded
                            :loading="acknowledgeInProcess"
                            @click="acknowledge">
                            <span>I Acknowledge</span>
                            <v-progress-circular
                                v-if="isServiceCreationInProgress"
                                class="ml-4"
                                :width="5"
                                :size="20"
                                indeterminate />
                        </v-btn>
                    </v-col>
                    <v-col
                        v-else-if="!sortedServices?.length || currentStep === 'service-launch'"
                        cols="12"
                        class="pa-0 wrapper-container"
                        style="width: 100%;">
                        <v-expansion-panels
                            v-model="panel"
                            class="ls-readonly-panels mb-6"
                            multiple
                            accordion
                            readonly>
                            <TopologySelection
                                class="hide"
                                :show-serverless-only="true"
                                :is-topology-selected="isTopologySelected"
                                @onRequirementOfDBSelect="onRequirementOfDBSelect"
                                @onTopologySelect="onTopologySelect" />
                            <CloudProviderSelection
                                v-if="isTopologySelected"
                                :get-locale-for="getLocaleFor"
                                :location="location"
                                :provider-type="providerType"
                                :current-tier="currentTier"
                                :topology="topologyName"
                                :is-disabled="shouldDisableCloudProvider"
                                @onCloudProviderSelect="onCloudProviderSelect"
                                @onRegionSelect="onRegionSelect"
                                @on-zone-select="onZoneSelection" />
                            <ConfigurationSelection
                                v-show="canConfigure"
                                class="hide"
                                :instance-data="filteredInstances"
                                :replicas="replicas"
                                :default-storage-size="selectedInstanceDisk"
                                :is-replica-disabled="isReplicaDisabled"
                                :provider-type="providerType"
                                :volume-types="volumeTypes"
                                :default-iops-volume-type="iopsVolumeType"
                                :default-iops-volume-size="iopsVolumeSize"
                                :current-tier="currentTier"
                                :topology-range="topologyNodeRange"
                                :show-config-panel="showConfigPanel"
                                :scu-scale-option="scuScaleOption"
                                :topology="topologyName"
                                :storage-range="storageSizes"
                                :instance-cost="filteredInstanceCosts"
                                :is-disabled="shouldDisableInstanceSelection"
                                :show-replica-widget="shouldHaveReplicaWidget"
                                :selected-instance="selectedInstance"
                                :max-scale-instances="filteredMaxscaleInstances"
                                @onInstanceConfigSelect="onInstanceConfigSelect"
                                @onStorageSelect="onStorageSelect"
                                @onReplicaChange="onReplicaChange"
                                @onVolumeSizeChange="changeIopsVolumeSize"
                                @onVolumeTypeChange="changeIopsVolumeType"
                                @on-auto-txn-storage-toggle="onStorageAutoToggle"
                                @on-auto-node-toggle="onNodeAutoToggle"
                                @on-node-scale-type-selected="onAutoNodeScaleTypeSelection"
                                @on-node-scale-value-selected="onAutoNodeScaleValueSelection"
                                @on-txn-storage-selected="onAutoStorageScaleValueSelection"
                                @on-maxscale-toggle="onMaxScaleToggle"
                                @on-maxscale-node-selection="setMaxScaleNode"
                                @on-maxscale-size-selection="setMaxScaleSize"
                                @on-iops-change="onIopsChange"
                                @on-throughput-change="onThroughputChange"
                                @optionChange="updateAdditionalOption" />
                            <ServerSelection
                                :get-locale-for="getLocaleFor"
                                :server-list="serverVersions"
                                :default-service-name="serviceName"
                                :is-disabled="shouldDisableServiceAttribute"
                                :region="location"
                                :can-change-version="canChangeServerVersion"
                                :is-power-tier-user="isPowerTierUser"
                                :is-serverless="isServerless"
                                :show-version-selection="false"
                                @onMariaDBServerSelect="onMariaDBServerSelect"
                                @onServiceNameEntered="onServiceNameEntered"
                                @onMaintenanceWindowSelect="onMaintenanceWindowSelect" />
                            <OptionsSelection
                                v-show="additionalOptionsToShow.length"
                                class="hide"
                                :get-locale-for="getLocaleFor"
                                :options="additionalOptionsToShow"
                                :is-disabled="shouldDisableOtherAttributes"
                                @optionChange="updateAdditionalOption" />
                            <SecuritySelectionV2
                                v-if="$unleash.isFeatureEnabled('enable-portal-dual-endpoints')"
                                v-show="canSetSecurity"
                                class="hide"
                                :provider-type="providerType"
                                :is-s-sl-enabled="isSSlEnabled"
                                :show-ssl="canToggleSSL"
                                :is-psc-supported="canUsePSC"
                                :is-disabled="shouldDisableSecurity"
                                @onSSLToggle="onSSLToggle"
                                @on-service-security-select="handleServiceSecurity"
                                @on-secondary-endpoint-toggled="handleSecondaryEndpointAction"
                                @on-secondary-endpoint-current-ip-select="handleSecondaryEndpointIpSelect" />
                            <SecuritySelection
                                v-else
                                v-show="canSetSecurity"
                                class="hide"
                                :provider-type="providerType"
                                :is-s-sl-enabled="isSSlEnabled"
                                :has-ssl="canToggleSSL"
                                :is-psc-supported="canUsePSC"
                                :is-disabled="shouldDisableSecurity"
                                @onSSLToggle="onSSLToggle"
                                @on-service-security-select="handleServiceSecurity" />
                        </v-expansion-panels>
                        <v-btn
                            :class="['launch-button text-none', { 'launch-disabled': shouldDisableLaunch }]"
                            :dark="!shouldDisableLaunch"
                            :disabled="shouldDisableLaunch"
                            block
                            x-large
                            rounded
                            @click="launchService">
                            <v-icon
                                size="30"
                                class="mr-3">
                                $vuetify.icons.launchRocket
                            </v-icon>
                            <span>{{ getLocaleFor('launchService') }}</span>
                            <v-progress-circular
                                v-if="isServiceCreationInProgress"
                                class="ml-4"
                                :width="5"
                                :size="20"
                                indeterminate />
                        </v-btn>
                        <div
                            v-if="isMaxServerlessRuleViolated"
                            class="pa-2 text-caption text-center">
                            {{ getLocaleFor('maxFreeServerlessMessage') }}
                        </div>
                    </v-col>
                    <v-col
                        v-else
                        cols="12"
                        class="pa-0 wrapper-container"
                        style="width: 100%;">
                        <template v-if="currentStep === 'success-screen'">
                            <v-row>
                                <v-col
                                    cols="12"
                                    class="pb-0">
                                    <div class="align-center d-flex flex-column mb-16 mt-10">
                                        <span class="text-body-1">
                                            Vercel integration with your service is successfully done
                                        </span>
                                        <v-icon
                                            size="180"
                                            class="success--text mt-4">
                                            mdi-cloud-check-variant
                                        </v-icon>
                                    </div>
                                </v-col>
                            </v-row>
                            <v-btn
                                class="launch-button text-none"
                                dark
                                block
                                x-large
                                rounded
                                :loading="closeInProcess"
                                @click="complete">
                                <span>Close and Return to Vercel</span>
                            </v-btn>
                        </template>
                        <template v-if="currentStep === 'service-listing'">
                            <v-row>
                                <v-col
                                    cols="12"
                                    class="d-flex flex-column">
                                    <span class="font-weight-bold text-body-1">Link Service and Project</span>
                                    <span>Select the SkySQL DB Instance and Vercel Project to link.</span>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="5">
                                    <span>SkySQL Service</span>
                                    <v-select
                                        v-model="selectedService"
                                        :items="sortedServices"
                                        :hint="serviceHint"
                                        :loading="servicesLoading"
                                        item-text="name"
                                        item-value="id"
                                        label="Select service"
                                        outlined
                                        attach
                                        required
                                        persistent-hint
                                        return-object
                                        single-line>
                                        <template #item="{ item }">
                                            <div class="d-flex flex-column py-3">
                                                <span class="font-weight-bold mb-1">
                                                    {{ item.name }}
                                                </span>
                                                <div class="d-flex text-caption">
                                                    <cloud-provider
                                                        :size="18"
                                                        :provider="item.provider">
                                                        cloud
                                                    </cloud-provider>
                                                    <span class="ml-1">
                                                        {{ $help.getTopologyName(item.topology, true) }} | {{ item.region }}
                                                    </span>
                                                </div>
                                            </div>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col
                                    cols="2"
                                    class="d-flex align-center justify-center">
                                    <v-icon
                                        size="36"
                                        class="text--primary">
                                        mdi-arrow-left-right
                                    </v-icon>
                                </v-col>
                                <v-col cols="5">
                                    <span>Vercel Project</span>
                                    <v-select
                                        v-model="selectedProject"
                                        :items="vercelProjects"
                                        :hint="projectHint"
                                        :loading="isProjectFetching"
                                        item-text="name"
                                        item-value="id"
                                        label="Select project"
                                        outlined
                                        attach
                                        required
                                        persistent-hint
                                        return-object
                                        single-line>
                                        <template #item="{ item }">
                                            <div class="d-flex flex-column py-3">
                                                <span class="font-weight-medium pb-1">
                                                    {{ item.name }}
                                                </span>
                                                <span class="text-caption">
                                                    {{ item.id }}
                                                </span>
                                                <span class="text-caption">
                                                    {{ item.framework }}{{ item.nodeVersion ? ` | ${item.nodeVersion}` : '' }}
                                                </span>
                                            </div>
                                        </template>
                                    </v-select>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-btn
                                        color="primary"
                                        depressed
                                        rounded
                                        block
                                        x-large
                                        :loading="submitInProcess"
                                        :disabled="isSubmitDisabled"
                                        @click="submit">
                                        <span class="px-1 text-none"> {{ $tc('continue') }}</span>
                                        <v-icon>
                                            mdi-arrow-right
                                        </v-icon>
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <div class="d-flex align-center">
                                        <v-divider />
                                        <span class="px-6 text-md-body-1">or</span>
                                        <v-divider />
                                    </div>
                                </v-col>
                                <v-col>
                                    <v-btn
                                        outlined
                                        large
                                        rounded
                                        color="primary"
                                        class="launch-btn text-capitalize"
                                        :disabled="submitInProcess"
                                        @click="currentStep = 'service-launch'">
                                        <span class="pr-1">+</span>
                                        {{ isFreeTrialActive ? $t('Service.launchFree') : $t('Service.launchNew') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { CONFIG } from 'config/'
    import i18n from 'plugins/i18n'
    import to from 'await-to-js'
    import TopologySelection from 'components/launchService/TopologySelection.vue'
    import CloudProviderSelection from 'components/launchService/CloudProviderSelection.vue'
    import ConfigurationSelection from 'components/launchService/ConfigurationSelection.vue'
    import ServerSelection from 'components/launchService/ServerSelection.vue'
    import OptionsSelection from 'components/launchService/OptionsSelection.vue'
    import SecuritySelection from 'components/launchService/SecuritySelection.vue'
    import SecuritySelectionV2 from 'components/launchService/SecuritySelectionV2.vue'
    import CloudProvider from 'components/service/CloudProvider'
    import { getFlagIconForRegion } from 'utils/countries'
    import { createService } from 'services/dbServices'
    import { getTopologyOptions } from 'services/offerings'
    import { authCallback, connectService, getProjects } from 'services/integrations.js'
    import { mapToAutoScalePolicyObjects } from 'mappers/autoScaling'
    import {
        isStandAloneTopology,
        isPrimaryReplica,
        isServerlessTopology,
        getFilteredInstanceSizes,
        isProviderAWS
    } from 'utils/service'
    import { getLegalEntityIdentifier } from 'utils/billing'
    import { isPowerTier } from 'utils/organization'

    const validFetchUsageStates = [CONFIG.serviceStatus.READY]
    export default {
        name: 'ConnectVercel',
        components: {
            CloudProvider,
            TopologySelection,
            CloudProviderSelection,
            ConfigurationSelection,
            ServerSelection,
            OptionsSelection,
            SecuritySelection,
            SecuritySelectionV2,
        },
        data() {
            return {
                step: 'access-confirmation',
                selectedService: {},
                selectedProject: {},
                isTopologySelected: false,
                isTransactionType: false,
                typeOfService: '',
                topologyType: '',
                topologyIcon: '',
                providerType: 'aws',
                location: null,
                locationLabel: null,
                currentLocationIcon: null,
                panel: [0, 1, 2, 3, 4, 5],
                cloudLocationHash: {},
                currentInstanceRowSelected: null,
                isServiceNameError: false,
                serviceName: '',
                selectedInstanceDisk: 100,
                selectedServer: null,
                server: null,
                serviceObject: {},
                replicas: 1,
                isReplicaDisabled: true,
                isSSlEnabled: true,
                iopsVolumeType: null,
                iopsVolumeSize: 0,
                showConfigPanel: false,
                topologyName: '',
                additionalOptions: [],
                iopsRange: [],
                selectedInstance: null,
                nodeAutoScaleOptions: [],
                storageAutoScaleOptions: [],
                isNodeAutoScaleEnabled: false,
                isTxnStorageAutoScaleEnabled: false,
                isServiceCreationInProgress: false,
                whiteListOption: null,
                isMaxScaleRedundancyEnabled: false,
                selectedMaxScaleNode: 2,
                selectedMaxScaleSize: {},
                selectedMaintenanceWindow: '',
                filteredInstances: [],
                filteredInstanceCosts: [],
                filteredMaxscaleInstances: [],
                singleZone: '',
                isSecondaryEndpointOpted: false,
                secondaryEndpointIP: null,
                selectedGp3Iops: 0,
                selectedGp3Throughput: 0,
                acknowledgeInProcess: false,
                submitInProcess: false,
                isProjectFetching: false,
                closeInProcess: false,
                vercelProjects: [],
            }
        },
        computed: {
            ...mapGetters([
                'services',
                'servicesLoading',
                'servicesInitialized',
                'isFreeTrialActive',
                'user',
                'instanceSizes',
                'serverVersions',
                'currentProjectId',
                'currentTier',
                'storageSizes',
                'nodeSizes',
                'iopsValues',
                'maintenanceWindowList',
                'maxScaleInstanceSizes',
                'availabilityZones',
                'volumeTypes',
                'billingRecord',
                'offerings'
            ]),
            currentStep: {
                get() {
                    return this.step
                },
                set(value) {
                    if (value === 'service-listing' && !this.sortedServices?.length) {
                        value = 'service-launch'
                    }
                    this.step = value
                    if (value === 'service-listing') {
                        this.fetchVercelProjects()
                    }
                },
            },
            sortedServices() {
                const clonedServices = this.services.filter(
                    service => isServerlessTopology(service?.topology)
                )?.filter(
                    service => validFetchUsageStates.includes(service?.status)
                )
                clonedServices.sort((serviceA, serviceB) => serviceB.created_on - serviceA.created_on)
                return clonedServices
            },
            serviceHint() {
                return this.$lodash.isEmpty(this.selectedService) ?
                    '' : `${this.selectedService.provider} | ${this.selectedService.region}`
            },
            projectHint() {
                let hint = ''
                if (!this.$lodash.isEmpty(this.selectedProject)) {
                    hint = `${this.selectedProject.id} | ${this.selectedProject.framework}`
                    if (this.selectedProject.nodeVersion) {
                        hint += ` | ${this.selectedProject.nodeVersion}`
                    }
                }
                return hint
            },
            isSubmitDisabled() {
                return this.$lodash.isEmpty(this.selectedService) ||
                    this.$lodash.isEmpty(this.selectedProject) ||
                    this.servicesLoading || this.isProjectFetching
            },
            localePathForLaunchService() {
                return 'launchService'
            },
            isServerless() {
                return isServerlessTopology(this.topologyName)
            },
            isAWSProvider() {
                return isProviderAWS(this.providerType)
            },
            shouldDisableCloudProvider() {
                return !this.isTopologySelected
            },
            shouldDisableInstanceSelection() {
                return !this.location
            },
            shouldDisableServiceAttribute() {
                return !this.selectedInstance
            },
            shouldDisableOtherAttributes() {
                return !this.serviceName
            },
            shouldHaveReplicaWidget() {
                return !isStandAloneTopology(this.topologyName)
            },
            topologyNodeRange() {
                if (isPrimaryReplica(this.topologyName)) {
                    return this.nodeSizes.map(node => node - 1)
                }
                return this.nodeSizes
            },
            shouldDisableSecurity() {
                return !this.serviceName
            },
            isPowerTierUser() {
                return isPowerTier(this.currentTier)
            },
            legalEntityIdentifier() {
                return getLegalEntityIdentifier(this.billingRecord)
            },
            isAlreadyHavingServerlessService() {
                return this.services?.some(service => isServerlessTopology(service.topology))
            },
            shouldDisableLaunch() {
                return this.isServiceNameError || this.isMaxServerlessRuleViolated
            },
            isMaxServerlessRuleViolated() {
                return (
                    this.isServerless
                    && this.isAlreadyHavingServerlessService
                    && !this.user?.isEmployee
                )
            },
            canToggleSSL() {
                return !this.isServerless
            },
            canChangeServerVersion() {
                return !this.isServerless
            },
            canConfigure() {
                return true
            },
            canUsePSC() {
                return !this.isServerless
            },
            canSetSecurity() {
                if (this.isServerless && !this.$unleash.isFeatureEnabled('enable-serverless-allowlist')) {
                    return false
                }
                return true
            },
            canConfigureForServerless() {
                return this.isServerless &&
                    !this.isFreeTrialActive &&
                    this.$unleash.isFeatureEnabled('enable-serverless-storage-scaling')
            },
            offering() {
                return this.offerings?.find(offering => offering.productMapping === this.typeOfService) || {}
            },
            scuScaleOption() {
                return this.isServerless ?
                    this.additionalOptions?.find(option => option.name === 'scale_options') : null
            },
            additionalOptionsToShow() {
                return this.additionalOptions?.filter(option => option.name !== 'scale_options') || []
            },
        },
        watch: {
            sortedServices(val) {
                if (val?.length) {
                    this.selectedService = val[0]
                }
            },
            storageSizes: {
                handler() {
                    this.resetStorageInstanceStep()
                },
                immediate: true,
            },
        },
        methods: {
            ...mapActions([
                'getInstanceSizes',
                'getServerVersions',
                'fetchServices',
                'fetchProjects',
                'fetchStorageSizes',
                'fetchNodeSizes',
                'fetchIops',
                'createAutoScalePolicies',
                'fetchVerticalScaleNodeSizes',
                'getMaxScaleInstanceSizes',
                'fetchMaintenanceWindowList',
                'fetchAvailabilityZones',
                'fetchVolumeTypes',
                'fetchBillingRecord',
                'fetchOfferings',
                'fetchOfferingMeters'
            ]),
            onRequirementOfDBSelect(dbDetails) {
                this.resetOptions()
                this.isTransactionType = dbDetails.name === this.$config.TRANSACTIONAL
                this.typeOfService = dbDetails.name
            },
            async onTopologySelect(topology) {
                await this.fetchOfferingMeters(this.offering?.offeringId)
                const currentSelectedTopology = this.topologyName
                this.isTxnStorageAutoScaleEnabled = false
                this.storageAutoScaleOptions = []
                this.resetOptions()
                this.showConfigPanel = false
                const { name, icon, } = topology
                this.topologyName = name
                this.topologyType = name
                this.topologyIcon = icon

                if (currentSelectedTopology !== this.topologyName && this.isServerless) {
                    this.providerType = this.$config.cloudProvider.GCP
                }

                this.isReplicaDisabled = topology.display_name.toLowerCase().includes('single')
                await this.checkAdditionalOptions(topology.name)

                if (this.$unleash.isFeatureEnabled('enable-portal-auto-tune')) {
                    const autoTuneOptionName = 'auto-tune'
                    this.isTransactionType ? this.addAdditionalOption({
                        name: autoTuneOptionName,
                        label: 'enableAutoTuning',
                        hint: 'autoTuneDescription',
                    }) : this.removeAdditionalOption(autoTuneOptionName)
                }

                this.isTopologySelected = true
                this.fetchStorageSizes(name)
                this.setNodeSizesForTopology(name)
                this.fetchVolumeTypes(this.providerType)
                this.getServerVersions(this.topologyName)
                if (this.$unleash.isFeatureEnabled('enable-portal-single-zone') && this.$lodash.isEmpty(this.availabilityZones)) {
                    this.fetchAvailabilityZones()
                }
            },
            onCloudProviderSelect(type) {
                this.resetOptions()
                this.providerType = type

                if (type === this.$config.cloudProvider.AWS) {
                    this.changeIopsVolumeType(this.iopsVolumeType)
                } else {
                    this.deleteFromServiceObject('volume_iops')
                    this.deleteFromServiceObject('volume_type')
                }
                this.fetchVolumeTypes(type)
            },
            async onRegionSelect(region) {
                this.resetOptions()
                this.showConfigPanel = false
                this.location = region.name
                this.locationLabel = region.label
                this.currentLocationIcon = `$verify.icons.${getFlagIconForRegion(region.name)}`

                await Promise.all([
                    this.$lodash.isEmpty(this.instanceSizes) && this.getInstanceSizes(),

                    this.isAWSProvider && this.$lodash.isEmpty(this.iopsValues) && this.fetchIops(),
                    this.isPowerTierUser && this.$lodash.isEmpty(this.maxScaleInstanceSizes) && this.fetchMaxScaleSizes()
                ])

                this.filterInstances()
                this.filterMaxScaleInstances()

                if (this.isAWSProvider) {
                    this.setDefaultIops()
                }

                this.showConfigPanel = true
            },
            async onInstanceConfigSelect(instance) {
                if (!instance) return
                this.selectedInstance = instance

                const { TRANSACTIONAL, ANALYTICAL, } = this.$config

                await Promise.all([
                    this.fetchVerticalScaleNodeSizes({
                        // eslint-disable-next-line camelcase
                        service_type: this.isTransactionType ? TRANSACTIONAL : ANALYTICAL,
                        provider: this.providerType,
                        ...(this.isAWSProvider && { architecture: instance.architecture, }),
                        // eslint-disable-next-line camelcase
                        min_node_size: this.selectedInstance.name,
                    }),
                    this.$lodash.isEmpty(this.maintenanceWindowList) && this.fetchMaintenanceWindowList()
                ])
            },
            onStorageSelect(size) {
                this.selectedInstanceDisk = size
            },
            onReplicaChange(count) {
                this.replicas = isPrimaryReplica(this.topologyName) ? ++count : count
            },
            onMariaDBServerSelect(server) {
                if (!server) return
                this.server = server
                this.selectedServer = server.display_name
            },
            onServiceNameEntered(serviceNameObj) {
                this.isServiceNameError = serviceNameObj.hasErrors
                this.serviceName = serviceNameObj.name
            },
            getLocaleFor(key) {
                return i18n.t(`${this.localePathForLaunchService}.${key}`)
            },
            isValidAutoScaleConfig() {
                if (
                    this.isTxnStorageAutoScaleEnabled && (
                        !this.storageAutoScaleOptions.length ||
                        this.storageAutoScaleOptions[0].value <= this.selectedInstanceDisk
                    )) {
                    return false
                } else {
                    return true
                }
            },
            async launchService() {
                if (!this.isValidAutoScaleConfig()) {
                    this.$store.commit('showMessage', { text: this.getLocaleFor('autoScaleConfigError'), type: 'error', })
                    return
                }
                this.isServiceCreationInProgress = true
                this.finalizeServiceObjectForLaunch()
                // Make a post and redirect on success
                const [error, response] = await to(createService(this.serviceObject))

                if (error) {
                    // TODO:: Need to work on showing the error UX
                    this.$store.commit('showMessage', { text: error.response.data.errors[0].message, type: 'error', })
                } else {
                    this.fetchServices()
                    const actions = this.setupAutoScaling()
                    if (actions.length) {
                        // eslint-disable-next-line camelcase
                        let scalingPolicies = { service_name: this.isServerless ? response.id : response.name, service_id: response.id, actions, }
                        await this.createAutoScalePolicies(scalingPolicies)
                    }
                    this.currentStep = 'service-listing'
                }
                this.isServiceCreationInProgress = false
            },
            finalizeServiceObjectForLaunch() {
                this.appendToServiceObject('project_id', this.currentProjectId)
                this.appendToServiceObject('service_type', this.typeOfService)
                this.appendToServiceObject('topology', this.topologyName)
                this.appendToServiceObject('provider', this.providerType)
                this.appendToServiceObject('region', this.location)
                this.appendToServiceObject('name', this.serviceName)
                if (this.canSetSecurity && this.whiteListOption) {
                    this.setupServiceObjectForWhiteListing()
                }
                if (this.$unleash.isFeatureEnabled('enable-portal-dual-endpoints') && this.isSecondaryEndpointOpted) {
                    this.setupServiceObjectForSecondaryEndpoint()
                }
                if (this.canConfigureForServerless) {
                    this.appendToServiceObject('storage', this.selectedInstanceDisk)
                }
                this.appendToServiceObject('ssl_enabled', this.isSSlEnabled)
                this.appendToServiceObject('version', this.server.name)
                if (this.additionalOptions.length) {
                    this.additionalOptions.forEach((option) => {
                        if (option.name === 'scale_options') {
                            this.appendToServiceObject(
                                option.name,
                                {
                                    'min_compute': option.value[0],
                                    'max_compute': option.value[1],
                                }
                            )
                        } else {
                            this.appendToServiceObject(option.name, option.value)
                        }
                    })
                }
                if (this.isMaxScaleRedundancyEnabled && this.setMaxScaleSize) {
                    this.appendToServiceObject('maxscale_nodes', this.selectedMaxScaleNode)
                    this.appendToServiceObject('maxscale_size', this.selectedMaxScaleSize.name)
                }
                if (this.$unleash.isFeatureEnabled('enable-portal-single-zone') && this.singleZone) {
                    this.appendToServiceObject('availability_zone', this.singleZone)
                }
            },
            setupServiceObjectForWhiteListing() {
                const { name, value, } = this.whiteListOption[0]
                if (name === 'ip-whitelist') {
                    if (value) this.appendToServiceObject('allow_list', [{ 'ip': value, }])
                    this.deleteFromServiceObject('endpoint_mechanism')
                    this.deleteFromServiceObject('endpoint_allowed_accounts')
                } else {
                    // private link/psc setup
                    this.appendToServiceObject('endpoint_mechanism', this.$config.ENDPOINT_MECHANISM_PRIVATELINK)
                    // Empty array as launch has no option to select project id
                    this.appendToServiceObject('endpoint_allowed_accounts', [])
                }
            },
            appendToServiceObject(key, value) {
                this.serviceObject[key] = value
            },
            deleteFromServiceObject(key) {
                delete this.serviceObject[key]
            },
            onSSLToggle(value) {
                this.isSSlEnabled = !value
            },
            changeIopsVolumeSize(size) {
                // Handle iops volume size change
                this.iopsVolumeSize = size
            },
            changeIopsVolumeType(type) {
                this.iopsVolumeType = type
            },
            async checkAdditionalOptions(topologyName) {
                this.additionalOptions = []
                const [error, response] = await to(getTopologyOptions(topologyName))
                if (error || !response) {
                    return
                }

                if (response.nosql_available) {
                    this.addAdditionalOption({
                        name: 'nosql_available',
                        type: 'checkbox',
                        value: false,
                        label: 'enableNoSQL',
                        hint: 'noSQLInfo',
                    })
                } else if (response.scale_options && this.$unleash.isFeatureEnabled('enable-serverless-scu-scaling')) {
                    this.addAdditionalOption({
                        name: 'scale_options',
                        type: 'range',
                        value: response.scale_options.cpu?.default,
                        props: {
                            min: response.scale_options.cpu?.range[0],
                            max: response.scale_options.cpu?.range[1],
                        },
                        label: 'scuScaling',
                        hint: 'scuScaleInfo',
                    })
                }
            },
            addAdditionalOption(option = {}) {
                if (this.additionalOptions.findIndex(({ name, }) => option.name === name) !== -1) {
                    return
                }
                this.additionalOptions.push(option)
            },
            removeAdditionalOption(optionName) {
                const optionIndex = this.additionalOptions.findIndex(option => option.name === optionName)
                if (optionIndex !== -1) {
                    this.additionalOptions.splice(optionIndex, 1)
                }
            },
            updateAdditionalOption({ optionName, value, }) {
                let additionalOption = this.additionalOptions.find(options => options.name === optionName)
                if (additionalOption) {
                    additionalOption.value = value
                }
            },
            async setNodeSizesForTopology(topology) {
                await this.fetchNodeSizes(topology)
                // Defaulting the nodes to second value coming
                // from API
                // In case of analytics only single value is supported
                if (this.nodeSizes && this.nodeSizes.length) {
                    const defaultReplicaSize = this.getDefaultNodeSizeForTopology(this.topologyName, this.nodeSizes)
                    this.replicas = defaultReplicaSize
                }
            },
            onAutoNodeScaleTypeSelection(scaleType) {
                // We support single scale option, so pop out existing
                if (this.nodeAutoScaleOptions.length) this.nodeAutoScaleOptions.pop()
                this.nodeAutoScaleOptions.push({ type: scaleType, })
            },
            onAutoNodeScaleValueSelection(scaleValue) {
                this.nodeAutoScaleOptions[0].maxValue = scaleValue
            },
            onAutoStorageScaleValueSelection(storageValue) {
                // We support single scale option, so pop out existing
                if (this.storageAutoScaleOptions.length) this.storageAutoScaleOptions.pop()
                this.storageAutoScaleOptions.push({
                    type: this.$config.AUTO_STORAGE_SCALE,
                    value: storageValue,
                })
            },
            onStorageAutoToggle(isChecked) {
                this.isTxnStorageAutoScaleEnabled = isChecked
                if (!isChecked) this.storageAutoScaleOptions.pop()
            },
            onNodeAutoToggle(isChecked) {
                this.isNodeAutoScaleEnabled = isChecked
                if (!isChecked) this.nodeAutoScaleOptions.pop()
            },
            setupAutoScaling() {
                // Is Node Scaling enabled
                let autoScalePolicies = []
                if (this.isNodeAutoScaleEnabled && this.nodeAutoScaleOptions.length) {
                    if (this.nodeAutoScaleOptions[0].type === this.$config.AUTO_VERTICAL_SCALE) {
                        this.nodeAutoScaleOptions[0].minValue = this.serviceObject.size
                    } else {
                        this.nodeAutoScaleOptions[0].minValue = this.serviceObject.nodes
                    }
                    autoScalePolicies.push(this.nodeAutoScaleOptions.map(mapToAutoScalePolicyObjects))
                }

                // Is storage Scaling enabled
                if (this.isTxnStorageAutoScaleEnabled && this.storageAutoScaleOptions.length) {
                    autoScalePolicies.push(this.storageAutoScaleOptions.map(mapToAutoScalePolicyObjects))
                }
                return autoScalePolicies.flat()
            },
            resetOptions() {
                this.singleZone = ''
                this.resetStorageInstanceStep()
            },
            resetTopologyType() {
                this.topologyName = ''
                this.topologyType = ''
                this.isTopologySelected = false
            },
            resetCloudProviderStep() {
                this.providerType = ''
            },
            resetRegionSelectionStep() {
                this.location = null
                this.locationLabel = ''
                this.currentLocationIcon = ''
                this.showConfigPanel = false
            },
            resetStorageInstanceStep() {
                if (this.isServerless && this.isFreeTrialActive) {
                    this.selectedInstanceDisk = 10
                } else {
                    this.selectedInstanceDisk = this.canConfigureForServerless ? (this.storageSizes?.[0] || 20) : 100
                }
                if (this.topologyName && this.nodeSizes) {
                    this.replicas = this.getDefaultNodeSizeForTopology(this.topologyName, this.nodeSizes)
                }
                this.setDefaultIops()
            },
            resetServiceAttributeStep() {
                this.selectedServer = null
                this.isSSlEnabled = true
            },
            resetAdditionalOptions() {
                this.additionalOptions = []
            },
            resetSecurityOptions() {
                this.isSSlEnabled = true
                this.whiteListOption = null
            },
            // Get default node size for topology
            // to initialize the launch flow
            getDefaultNodeSizeForTopology(topology, nodeSizes) {
                if (isPrimaryReplica(topology)) {
                    return 1
                }
                return nodeSizes[0] || 1
            },
            handleServiceSecurity(val) {
                this.whiteListOption = val
            },
            onMaxScaleToggle(isChecked) {
                this.isMaxScaleRedundancyEnabled = isChecked
            },
            fetchMaxScaleSizes() {
                this.getMaxScaleInstanceSizes({
                    type: this.$config.INSTANCE_SIZE_TYPE.PROXY,
                })
            },
            setMaxScaleNode(val) {
                this.selectedMaxScaleNode = val
            },
            setMaxScaleSize(val) {
                this.selectedMaxScaleSize = val
            },
            onMaintenanceWindowSelect(val) {
                this.selectedMaintenanceWindow = val
            },
            filterInstances() {
                // Filter instances for the current user selection
                // from list of all the sizes fetched from API
                this.filteredInstances = getFilteredInstanceSizes(this.instanceSizes, {
                    provider: this.providerType,
                    serviceType: this.typeOfService,
                    topology: this.topologyName,
                })
            },
            filterMaxScaleInstances() {
                this.filteredMaxscaleInstances = getFilteredInstanceSizes(this.maxScaleInstanceSizes, {
                    provider: this.providerType,
                    serviceType: this.typeOfService,
                    topology: this.topologyName,
                })
            },
            setDefaultIops() {
                this.changeIopsVolumeSize(this.$typy(this, 'iopsValues[0].default_iops_per_gb').safeNumber || this.$config.IOPS_DEFAULT)
            },
            onZoneSelection(zone) {
                this.singleZone = zone
            },
            handleSecondaryEndpointAction(isSecondaryEndpointOpted) {
                this.isSecondaryEndpointOpted = isSecondaryEndpointOpted
            },
            handleSecondaryEndpointIpSelect(selectedIP) {
                this.secondaryEndpointIP = selectedIP
            },
            setupServiceObjectForSecondaryEndpoint() {
                const name = this.$help.getUniqueString(16)
                this.appendToServiceObject('endpoints', [{ name, }])
                if (this.secondaryEndpointIP) {
                    this.appendToServiceObject('allow_list', [{ 'ip': this.secondaryEndpointIP, }])
                }
            },
            onIopsChange(val) {
                this.selectedGp3Iops = val
            },
            onThroughputChange(val) {
                this.selectedGp3Throughput = val
            },
            setDefaultServiceName() {
                const suffix = this.$help.getRandomString(4).toLowerCase()
                const defaultName = `${this.user.firstName?.toLowerCase()}-${this.user.lastName?.toLowerCase()}-${suffix}`
                const validation = /^[a-z][a-z0-9-]{0,23}$/
                if (validation.test(defaultName)) {
                    this.serviceName = defaultName
                } else {
                    this.serviceName = `my-service-${suffix}`
                }
            },
            complete() {
                this.closeInProcess = true
                try {
                    const searchParams = localStorage?.getItem('__VERCEL_SEARCH__')
                    if (!searchParams?.length) return

                    const params = new URLSearchParams(searchParams)
                    const next = params.get('next')
                    window.location.href = next
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                } finally {
                    this.closeInProcess = false
                }
            },
            async fetchVercelProjects() {
                this.isProjectFetching = true
                try {
                    const [error, response] = await to(getProjects())
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                    } else {
                        this.vercelProjects = response
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                } finally {
                    this.isProjectFetching = false
                }
            },
            async acknowledge() {
                this.acknowledgeInProcess = true
                try {
                    const searchParams = localStorage?.getItem('__VERCEL_SEARCH__')
                    if (!searchParams?.length) return
                    const queryObject = Object.fromEntries(new URLSearchParams(searchParams))
                    queryObject.installationId = queryObject.configurationId

                    // eslint-disable-next-line no-unused-vars
                    const [error, response] = await to(authCallback(queryObject))
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                    } else {
                        this.currentStep = 'service-listing'
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                } finally {
                    this.acknowledgeInProcess = false
                }
            },
            async submit() {
                this.submitInProcess = true
                try {
                    const searchParams = localStorage?.getItem('__VERCEL_SEARCH__')
                    if (!searchParams?.length || !this.selectedService || !this.selectedProject) return
                    const params = new URLSearchParams(searchParams)
                    const installationId = params.get('configurationId')

                    // eslint-disable-next-line no-unused-vars
                    const [error, response] = await to(connectService(
                        installationId,
                        {
                            serviceId: this.selectedService.id,
                            projectId: this.selectedProject.id,
                        }
                    ))
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                    } else {
                        this.currentStep = 'success-screen'
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.unknown'), type: this.$config.messageType.ERROR, })
                } finally {
                    this.submitInProcess = false
                }
            },
        },
        async created() {
            if (this.$lodash.isEmpty(this.currentProjectId)) this.fetchProjects()
            if (this.$lodash.isEmpty(this.offerings)) this.fetchOfferings()
            this.fetchBillingRecord()
            if (!this.services?.length) {
                await this.fetchServices()
            }
            this.setDefaultServiceName()
        },
        destroyed() {},
    }
</script>
